import _ from 'lodash';
import { useCallback } from 'react';
import { useAppBridge } from 'scbk-services/bridge';
import { useChannelContext } from 'scbk-services/channel';
import { Channel } from 'scbk-services/config';
import { useRestContext } from 'scbk-services/http';
import { useRouterHistory } from 'scbk-services/router';
import { useUserContextStore } from 'scbk-services/shared-context/stores/userContextStore.ts';
import type { UserContext } from 'scbk-services/types/shared-context/user-context';

const useUserContext = () => {
    const resetUserCommonContext = useUserContextStore((state) => state.resetUserCommonContext);
    const setUserCommonContext = useUserContextStore((state) => state.setUserCommonContext);
    const updateUserCommonContext = useUserContextStore((state) => state.updateUserCommonContext);

    const getUserAdditionalContext = useUserContextStore((state) => state.getAdditionalContext);
    const resetUserAdditionalContext = useUserContextStore(
        (state) => state.resetUserAdditionalContext,
    );
    const setUserAdditionalContext = useUserContextStore((state) => state.setUserAdditionalContext);
    const updateUserAdditionalContext = useUserContextStore(
        (state) => state.updateUserAdditionalContext,
    );

    const getCommonContext = (): UserContext.CommonContext => {
        return useUserContextStore.getState().getCommonContext();
    };

    const resetCommonContext = (): void => {
        resetUserCommonContext();
    };

    const setCommonContext = (context: UserContext.CommonContext): void => {
        setUserCommonContext(context);
    };

    const updateCommonContext = (context: UserContext.CommonContext): void => {
        updateUserCommonContext(context);
    };

    const getAdditionalContext = (): Record<string, unknown> => {
        return getUserAdditionalContext();
    };

    const resetAdditionalContext = (): void => {
        resetUserAdditionalContext();
    };

    const setAdditionalContext = (context: Record<string, unknown>): void => {
        setUserAdditionalContext(context);
    };

    const updateAdditionalContext = (context: Record<string, unknown>): void => {
        updateUserAdditionalContext(context);
    };

    /**
     * 사용자 로그인 여부를 획득한다.
     * 실시간으로 값을 가져오도록 수정
     * @returns
     */
    const isLoginUser = (): boolean => {
        return _.isNotEmpty(useUserContextStore.getState().getCommonContext().custName)
            ? true
            : false;
    };

    return {
        getCommonContext,
        resetCommonContext,
        setCommonContext,
        updateCommonContext,
        getAdditionalContext,
        resetAdditionalContext,
        setAdditionalContext,
        updateAdditionalContext,
        isLoginUser,
    };
};

export default useUserContext;

export const useUserData = () => {
    const { resetCommonContext, resetAdditionalContext } = useUserContext();
    const { resetDynamicContext } = useRestContext();
    const { doNSetDeviceStorage, doNSetWebviewStorage } = useAppBridge();
    const { doNLogout } = useAppBridge();
    const { channel } = useChannelContext();
    const { storageKey } = useRouterHistory();

    /**
     * 유저 저장 정보 초기화
     */
    const clearUserData = useCallback(
        async ({ nativeLogout }: { nativeLogout: boolean } = { nativeLogout: false }) => {
            resetCommonContext();
            resetAdditionalContext();
            resetDynamicContext();
            doNSetWebviewStorage('loginCertInfo', ''); // MA40 로그인 인증서 정보 제거

            if (channel === Channel.IB) {
                // stack 및 localStorage 클리어
                localStorage.removeItem(storageKey);
            }

            if (nativeLogout) {
                await doNLogout('N');
            }
        },
        [
            resetCommonContext,
            resetAdditionalContext,
            resetDynamicContext,
            doNSetWebviewStorage,
            doNSetDeviceStorage,
            channel,
        ],
    );

    return {
        clearUserData,
    };
};
